/**
 * Compliance & tax documents: statutory returns, challans, and Form 16.
 *
 * TWO AUDIENCES IN ONE ROUTER, SEPARATED BY PERMISSION. `/compliance/*` is the statutory hub HR and
 * payroll work in. `/compliance/me/*` is the employee viewport — their own YTD contributions and
 * their own Form 16 — and it needs `statutory.own:read` at self scope. Holding
 * `statutory.filing:manage` does not widen the second; it is a different question entirely.
 *
 * A FILING IS GENERATED ONCE AND THEN FROZEN. `generate` is idempotent and, on a filing that has
 * already been signed off, refused outright: the figures a named officer put their name to cannot
 * be replaced by a later recalculation of the same month. Regenerating an unsigned filing is fine
 * and replaces its file and its lines wholesale.
 *
 * THREE DECISIONS, THREE ROUTES, THREE PERMISSIONS — `signOff`, `approveRemittance` and
 * `recordFiled`. That is not ceremony. Preparing a return, attesting to it, and releasing ₹42 lakh
 * against it are things different people do, and a single "submit" that did all three would defeat
 * the separation the permissions exist to create. The API additionally refuses a sign-off by the
 * person who generated the filing — `can()` cannot express "different from that other column", so
 * the check lives at the write path (see routes/payroll-compliance.ts).
 *
 * NOTHING HERE FILES ANYTHING WITH A GOVERNMENT. There is no EPFO, ESIC or TRACES client. The file
 * is downloaded, a human uploads it, and `recordFiled` writes down the acknowledgement that came
 * back. `state: 'filed'` is a claim about what a person did.
 *
 * NO FILE URL IS EVER PERSISTED OR RETURNED IN A LIST. A download is a separate call that
 * re-authorises and mints a link expiring in minutes — an ECR carries every member's UAN and wage
 * (the same reasoning as the tax proofs, see payroll-tax.contract.ts).
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  CURRENCIES,
  FORM16_STATES,
  STATUTORY_EXCLUSION_REASONS,
  STATUTORY_FILE_FORMATS,
  STATUTORY_FILING_STATES,
  STATUTORY_RETURN_TYPES,
} from '../enums.js';
import {
  dateOnlySchema,
  decimalStringSchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({ 'idempotency-key': idempotencyKeySchema });

export const statutoryReturnTypeSchema = z.enum(STATUTORY_RETURN_TYPES);
export const statutoryFileFormatSchema = z.enum(STATUTORY_FILE_FORMATS);
export const statutoryFilingStateSchema = z.enum(STATUTORY_FILING_STATES);
export const statutoryExclusionReasonSchema = z.enum(STATUTORY_EXCLUSION_REASONS);
export const form16StateSchema = z.enum(FORM16_STATES);
const currencySchema = z.enum(CURRENCIES);

const filingParams = z.object({ filingId: uuidSchema });

// ---------------------------------------------------------------------------
// Filings
// ---------------------------------------------------------------------------

/** One row of the challan's account-wise split: EPF A/C 1, 2, 10, 21, 22; ESI's two halves; PT slabs. */
export const filingBreakdownRowSchema = z.object({
  account: z.string(),
  label: z.string(),
  amount: decimalStringSchema,
});
export type FilingBreakdownRow = z.infer<typeof filingBreakdownRowSchema>;

export const statutoryFilingSummarySchema = z.object({
  id: uuidSchema,
  legalEntityId: uuidSchema,
  legalEntityName: z.string(),
  returnType: statutoryReturnTypeSchema,
  /** "Employees Provident Fund (EPF ECR)" — resolved server-side so every surface reads alike. */
  returnLabel: z.string(),
  statutoryBody: z.string().nullable(),
  registrationRef: z.string().nullable(),
  fiscalYear: z.number().int(),
  periodStart: dateOnlySchema,
  periodEnd: dateOnlySchema,
  periodLabel: z.string(),
  dueOn: dateOnlySchema,
  /** Negative once the deadline has passed. Computed in the entity's timezone, never the browser's. */
  daysToDue: z.number().int(),
  state: statutoryFilingStateSchema,
  scopedEmployeeCount: z.number().int().nonnegative(),
  includedEmployeeCount: z.number().int().nonnegative(),
  excludedEmployeeCount: z.number().int().nonnegative(),
  employeeContribution: decimalStringSchema,
  employerContribution: decimalStringSchema,
  totalLiability: decimalStringSchema,
  currency: currencySchema,
  fileFormat: statutoryFileFormatSchema.nullable(),
  fileName: z.string().nullable(),
  /** Whether a file exists to download. The link itself is a separate, re-authorised call. */
  hasFile: z.boolean(),
  generatedByName: z.string().nullable(),
  generatedAt: instantSchema.nullable(),
  signedOffByName: z.string().nullable(),
  signedOffAt: instantSchema.nullable(),
  remittanceApprovedByName: z.string().nullable(),
  remittanceApprovedAt: instantSchema.nullable(),
  challanNumber: z.string().nullable(),
  challanCin: z.string().nullable(),
  remittedOn: dateOnlySchema.nullable(),
  acknowledgementRef: z.string().nullable(),
  filedOn: dateOnlySchema.nullable(),
  failureReason: z.string().nullable(),
  /**
   * What THIS caller may do to THIS filing, decided server-side. The screen renders these rather
   * than re-deriving a decision from permissions it holds — which is how a button that 403s on
   * click gets shipped.
   */
  canGenerate: z.boolean(),
  canSignOff: z.boolean(),
  canApproveRemittance: z.boolean(),
  canDownload: z.boolean(),
  /** Why a control is off, when the reason is worth saying: "you prepared this filing". */
  blockedReason: z.string().nullable(),
});
export type StatutoryFilingSummary = z.infer<typeof statutoryFilingSummarySchema>;

export const filingLineSchema = z.object({
  id: uuidSchema,
  employmentId: uuidSchema,
  personId: uuidSchema,
  employeeCode: z.string().nullable(),
  memberName: z.string(),
  /** Masked. The full UAN / ESIC number / PAN is never returned on a line. */
  identifierRef: z.string().nullable(),
  grossWage: decimalStringSchema,
  contributionWage: decimalStringSchema,
  employeeAmount: decimalStringSchema,
  employerAmount: decimalStringSchema,
  otherAmount: decimalStringSchema,
  currency: currencySchema,
  isIncluded: z.boolean(),
  exclusionReason: statutoryExclusionReasonSchema.nullable(),
  /** A sentence a human can act on: "UAN is not Aadhaar-seeded — seed it on the EPFO portal." */
  exclusionDetail: z.string().nullable(),
  detail: z.record(z.string()).nullable(),
});
export type FilingLine = z.infer<typeof filingLineSchema>;

export const statutoryFilingDetailSchema = statutoryFilingSummarySchema.extend({
  breakdown: z.array(filingBreakdownRowSchema),
  sourceCalculationVersionIds: z.array(uuidSchema),
  signOffNote: z.string().nullable(),
  remittedAmount: decimalStringSchema.nullable(),
  fileChecksum: z.string().nullable(),
  fileSizeBytes: z.number().int().nullable(),
  /** The included members, paginated. The error batch is its own route. */
  lines: z.array(filingLineSchema),
  lineTotal: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});
export type StatutoryFilingDetail = z.infer<typeof statutoryFilingDetailSchema>;

export const listFilingsQuerySchema = z.object({
  legalEntityId: uuidSchema.optional(),
  fiscalYear: z.coerce.number().int().optional(),
  /** A month inside the period, as `YYYY-MM-DD`. Matches any filing whose period contains it. */
  periodOn: dateOnlySchema.optional(),
  returnType: statutoryReturnTypeSchema.optional(),
  state: statutoryFilingStateSchema.optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(25),
});

export const filingListSchema = z.object({
  items: z.array(statutoryFilingSummarySchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});

/**
 * The four tiles above the table.
 *
 * `auditReadinessPercent` IS A RATIO, NOT A GRADE. It is the share of scoped members who carry the
 * identifiers their returns need — nothing more. It is deliberately not a composite score over
 * things like "were we late last quarter", because a single confident-looking percentage assembled
 * from unlike inputs is a number nobody can act on.
 */
export const complianceSummarySchema = z.object({
  fiscalYear: z.number().int(),
  periodLabel: z.string(),
  currency: currencySchema,
  totalStatutoryDue: decimalStringSchema,
  /** The earliest deadline among filings not yet remitted. Null when nothing is outstanding. */
  nextDueOn: dateOnlySchema.nullable(),
  scopedEmployeeCount: z.number().int().nonnegative(),
  identifierReadyCount: z.number().int().nonnegative(),
  filingsPendingSignOff: z.number().int().nonnegative(),
  filingsTotal: z.number().int().nonnegative(),
  /** The one that is waiting, named — "Karnataka PT Form 5A". Null when none is. */
  pendingSignOffLabel: z.string().nullable(),
  auditReadinessPercent: decimalStringSchema,
  /** How many members are missing which identifier — the readiness figure, itemised. */
  identifierGaps: z.array(
    z.object({
      reason: statutoryExclusionReasonSchema,
      label: z.string(),
      count: z.number().int().nonnegative(),
    }),
  ),
  /**
   * Deadlines that are not a generated return — the monthly TDS deposit and the Form 16 issue date —
   * falling in the next 45 days, soonest first. Optional so a server without it still satisfies the
   * contract.
   */
  upcomingDeadlines: z
    .array(
      z.object({
        key: z.enum(['tds_deposit', 'form16_issue']),
        label: z.string(),
        dueOn: dateOnlySchema,
      }),
    )
    .optional(),
});
export type ComplianceSummary = z.infer<typeof complianceSummarySchema>;

export const generateFilingBodySchema = z.object({
  legalEntityId: uuidSchema,
  returnType: statutoryReturnTypeSchema,
  /** Any date inside the period to file. The server resolves the period from the pay calendar. */
  periodOn: dateOnlySchema,
  fileFormat: statutoryFileFormatSchema,
  /**
   * Filter out members whose UAN is not Aadhaar-seeded into the error batch rather than writing
   * them into the file. Off is a legitimate choice for a layout that does not require seeding; the
   * consequence is a portal rejection, which is why the default is on.
   */
  validateAadhaarSeeding: z.boolean().default(true),
  /** Flag members with a missing or malformed PAN for section 206AA treatment. */
  checkNonPanDeduction: z.boolean().default(true),
});

export const signOffFilingBodySchema = z.object({
  note: z.string().trim().max(2000).optional(),
  /**
   * The liability the approver saw. A mismatch is a 409, not a silent approval of a different
   * number — the same reasoning as a payroll run's `approvedCalculationVersionId`.
   */
  expectedTotalLiability: decimalStringSchema,
});

export const approveRemittanceBodySchema = z.object({
  challanNumber: z.string().trim().min(1).max(64),
  challanCin: z.string().trim().min(1).max(64).optional(),
  remittedOn: dateOnlySchema,
  /** What actually left the account. Allowed to differ from the liability; the difference is kept. */
  remittedAmount: decimalStringSchema,
  note: z.string().trim().max(2000).optional(),
});

export const recordFiledBodySchema = z.object({
  acknowledgementRef: z.string().trim().min(1).max(128),
  filedOn: dateOnlySchema,
});

/** A short-lived link to the generated file. Nothing durable is ever stored or returned. */
export const filingDownloadSchema = z.object({
  downloadUrl: z.string(),
  fileName: z.string(),
  contentType: z.string(),
  expiresInSeconds: z.number().int().positive(),
  checksum: z.string().nullable(),
});

export const errorBatchSchema = z.object({
  filingId: uuidSchema,
  returnLabel: z.string(),
  items: z.array(filingLineSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  /** Excluded members grouped by reason, so the list can be worked one cause at a time. */
  byReason: z.array(
    z.object({
      reason: statutoryExclusionReasonSchema,
      label: z.string(),
      count: z.number().int().nonnegative(),
    }),
  ),
});
export type ErrorBatch = z.infer<typeof errorBatchSchema>;

// ---------------------------------------------------------------------------
// Form 16
// ---------------------------------------------------------------------------

export const form16QuarterSchema = z.object({
  quarter: z.enum(['Q1', 'Q2', 'Q3', 'Q4']),
  taxDeducted: decimalStringSchema,
  taxDeposited: decimalStringSchema,
  /** The 24Q acknowledgement the quarter was reported under, where one has been recorded. */
  acknowledgementRef: z.string().nullable(),
});
export type Form16Quarter = z.infer<typeof form16QuarterSchema>;

export const form16RowSchema = z.object({
  id: uuidSchema,
  employmentId: uuidSchema,
  personId: uuidSchema,
  employeeCode: z.string().nullable(),
  employeeName: z.string(),
  fiscalYear: z.number().int(),
  /** "FY 2023-24 (AY 2024-25)". */
  fiscalYearLabel: z.string(),
  state: form16StateSchema,
  grossSalary: decimalStringSchema,
  taxPayable: decimalStringSchema,
  taxDeposited: decimalStringSchema,
  currency: currencySchema,
  hasPartA: z.boolean(),
  hasPartB: z.boolean(),
  issuedAt: instantSchema.nullable(),
});
export type Form16Row = z.infer<typeof form16RowSchema>;

export const listForm16QuerySchema = z.object({
  legalEntityId: uuidSchema.optional(),
  fiscalYear: z.coerce.number().int(),
  state: form16StateSchema.optional(),
  search: z.string().trim().max(120).optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(25),
});

export const form16ListSchema = z.object({
  items: z.array(form16RowSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  /** The counts the distribution tab leads with. */
  counts: z.object({
    notGenerated: z.number().int().nonnegative(),
    generated: z.number().int().nonnegative(),
    issued: z.number().int().nonnegative(),
    revoked: z.number().int().nonnegative(),
  }),
});

export const generateForm16BodySchema = z.object({
  legalEntityId: uuidSchema,
  fiscalYear: z.number().int(),
  /** Empty means everyone in scope for the year. Naming employments regenerates only those. */
  employmentIds: z.array(uuidSchema).max(1000).optional(),
});

export const generateForm16ResultSchema = z.object({
  generated: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  /** Why each skipped employee was skipped. An empty result with no reasons would be a mystery. */
  skippedReasons: z.array(z.object({ reason: z.string(), count: z.number().int().nonnegative() })),
});

export const issueForm16BodySchema = z.object({
  legalEntityId: uuidSchema,
  fiscalYear: z.number().int(),
  employmentIds: z.array(uuidSchema).max(1000).optional(),
  /** Tell each employee their certificate is available. Default on; a re-issue may want it off. */
  notifyEmployees: z.boolean().default(true),
});

export const issueForm16ResultSchema = z.object({
  issued: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  notified: z.number().int().nonnegative(),
});

/** Part A comes from TRACES and is uploaded; the three-step handshake mirrors the tax proofs. */
export const uploadPartABodySchema = z.object({
  employmentId: uuidSchema,
  fiscalYear: z.number().int(),
  fileName: z.string().trim().min(1).max(255),
  contentType: z.literal('application/pdf'),
  sizeBytes: z.number().int().positive(),
});

export const uploadTicketSchema = z.object({
  storageKey: z.string(),
  uploadUrl: z.string(),
  requiredHeaders: z.record(z.string()),
  expiresInSeconds: z.number().int().positive(),
});

export const confirmPartABodySchema = z.object({
  employmentId: uuidSchema,
  fiscalYear: z.number().int(),
  storageKey: z.string(),
});

// ---------------------------------------------------------------------------
// The employee viewport
// ---------------------------------------------------------------------------

/**
 * "My statutory contributions" — the employee's own YTD figures.
 *
 * SUMMED FROM PERSISTED RESULTS PLUS THE OPENING BALANCE, never recomputed. An employee who joined
 * mid-year carries YTD figures from their previous employer's payroll through
 * `PayrollOpeningBalance`, and a summary that ignored them would be wrong for exactly the people
 * most likely to check.
 */
export const myStatutorySummarySchema = z.object({
  fiscalYear: z.number().int(),
  fiscalYearLabel: z.string(),
  currency: currencySchema,
  /** Null when this employee's entity has run no payroll yet this year — stated, not shown as zero. */
  throughPeriodLabel: z.string().nullable(),
  providentFund: z.object({
    isApplicable: z.boolean(),
    employeeShare: decimalStringSchema,
    employerPfShare: decimalStringSchema,
    employerPensionShare: decimalStringSchema,
    total: decimalStringSchema,
    /** Masked UAN, or null when none is on file — which is itself worth showing. */
    uanMasked: z.string().nullable(),
  }),
  employeeStateInsurance: z.object({
    isApplicable: z.boolean(),
    employeeShare: decimalStringSchema,
    employerShare: decimalStringSchema,
    esicNumberMasked: z.string().nullable(),
  }),
  professionalTax: decimalStringSchema,
  tax: z.object({
    totalDeducted: decimalStringSchema,
    panMasked: z.string().nullable(),
    quarters: z.array(form16QuarterSchema),
    /**
     * Whether the quarterly deposits this org has recorded reconcile with what was deducted. NOT a
     * claim about Form 26AS: this system has never read the employee's 26AS and does not pretend
     * to. It compares our own numbers, and says so.
     */
    isInternallyReconciled: z.boolean(),
  }),
  form16: z
    .object({
      fiscalYear: z.number().int(),
      fiscalYearLabel: z.string(),
      state: form16StateSchema,
      hasPartA: z.boolean(),
      hasPartB: z.boolean(),
      issuedAt: instantSchema.nullable(),
      deductorTan: z.string().nullable(),
    })
    .nullable(),
  /** Earlier years with an issued certificate, newest first. */
  archivedYears: z.array(z.object({ fiscalYear: z.number().int(), label: z.string() })),
});
export type MyStatutorySummary = z.infer<typeof myStatutorySummarySchema>;

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

export const payrollComplianceContract = c.router(
  {
    getSummary: {
      method: 'GET',
      path: '/compliance/summary',
      query: z.object({
        legalEntityId: uuidSchema.optional(),
        fiscalYear: z.coerce.number().int().optional(),
        periodOn: dateOnlySchema.optional(),
      }),
      responses: {
        200: complianceSummarySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'The statutory hub tiles for one entity and one cycle',
      metadata: routeMeta('payroll:read'),
    },

    listFilings: {
      method: 'GET',
      path: '/compliance/filings',
      query: listFilingsQuerySchema,
      responses: {
        200: filingListSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Statutory returns and their state, filtered',
      metadata: routeMeta('payroll:read'),
    },

    getFiling: {
      method: 'GET',
      path: '/compliance/filings/:filingId',
      pathParams: filingParams,
      query: z.object({
        page: z.coerce.number().int().positive().default(1),
        pageSize: z.coerce.number().int().positive().max(200).default(50),
      }),
      responses: {
        200: statutoryFilingDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'One filing: the challan split, and the members in it',
      metadata: routeMeta('payroll:read', { salaryPin: 'others' }),
    },

    generateFiling: {
      method: 'POST',
      path: '/compliance/filings/generate',
      headers: idempotencyHeadersSchema,
      body: generateFilingBodySchema,
      responses: {
        201: statutoryFilingDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** No approved payroll for the period, or no published statutory rule to compute under. */
        404: errorBodySchema,
        /** Already signed off — a signed return is not regenerated, it is corrected and refiled. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Compute a statutory return from the period payroll and write its file',
      metadata: routeMeta('statutory.filing:manage'),
    },

    getErrorBatch: {
      method: 'GET',
      path: '/compliance/filings/:filingId/error-batch',
      pathParams: filingParams,
      query: z.object({
        reason: statutoryExclusionReasonSchema.optional(),
        page: z.coerce.number().int().positive().default(1),
        pageSize: z.coerce.number().int().positive().max(200).default(50),
      }),
      responses: {
        200: errorBatchSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'The members excluded from the file, and why',
      metadata: routeMeta('payroll:read', { salaryPin: 'others' }),
    },

    downloadFiling: {
      method: 'POST',
      path: '/compliance/filings/:filingId/download',
      pathParams: filingParams,
      body: z.object({}),
      responses: {
        200: filingDownloadSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Nothing generated yet. */
        409: errorBodySchema,
      },
      summary: 'Mint a short-lived link to the generated file, re-authorising first',
      metadata: routeMeta('payroll:read', { salaryPin: 'others' }),
    },

    signOffFiling: {
      method: 'POST',
      path: '/compliance/filings/:filingId/sign-off',
      pathParams: filingParams,
      headers: idempotencyHeadersSchema,
      body: signOffFilingBodySchema,
      responses: {
        200: statutoryFilingDetailSchema,
        401: errorBodySchema,
        /** Held by the person who generated it — maker and checker cannot be one person. */
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not generated, already signed, or the liability moved since it was read. */
        409: errorBodySchema,
      },
      summary: 'Authorise a generated return as a statutory declaration',
      metadata: routeMeta(['payroll:read', 'statutory.filing:approve'], { salaryPin: 'others' }),
    },

    approveRemittance: {
      method: 'POST',
      path: '/compliance/filings/:filingId/remittance',
      pathParams: filingParams,
      headers: idempotencyHeadersSchema,
      body: approveRemittanceBodySchema,
      responses: {
        200: statutoryFilingDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not signed off yet — nothing is released against an unauthorised return. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Release the challan and record what was paid',
      metadata: routeMeta(['payroll:read', 'statutory.remittance:approve'], { salaryPin: 'others' }),
    },

    recordFiled: {
      method: 'POST',
      path: '/compliance/filings/:filingId/filed',
      pathParams: filingParams,
      body: recordFiledBodySchema,
      responses: {
        200: statutoryFilingDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Record the portal acknowledgement a human received',
      metadata: routeMeta(['payroll:read', 'statutory.filing:manage'], { salaryPin: 'others' }),
    },

    listForm16: {
      method: 'GET',
      path: '/compliance/form16',
      query: listForm16QuerySchema,
      responses: {
        200: form16ListSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Form 16 certificates for a fiscal year, and their distribution state',
      metadata: routeMeta('payroll:read', { salaryPin: 'others' }),
    },

    generateForm16: {
      method: 'POST',
      path: '/compliance/form16/generate',
      headers: idempotencyHeadersSchema,
      body: generateForm16BodySchema,
      responses: {
        200: generateForm16ResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Compute Part B from the year of persisted payroll results',
      metadata: routeMeta('form16:generate'),
    },

    issueForm16: {
      method: 'POST',
      path: '/compliance/form16/issue',
      headers: idempotencyHeadersSchema,
      body: issueForm16BodySchema,
      responses: {
        200: issueForm16ResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Release generated certificates to the employees they belong to',
      metadata: routeMeta('form16:issue'),
    },

    authorisePartAUpload: {
      method: 'POST',
      path: '/compliance/form16/part-a/upload',
      body: uploadPartABodySchema,
      responses: {
        200: uploadTicketSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Authorise a TRACES Part A upload — step one of the three-step handshake',
      metadata: routeMeta('form16:generate', { ai: 'hidden' }),
    },

    confirmPartAUpload: {
      method: 'POST',
      path: '/compliance/form16/part-a/confirm',
      body: confirmPartABodySchema,
      responses: {
        200: form16RowSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Storage says the object is absent, too large, or not what was declared. */
        422: errorBodySchema,
      },
      summary: 'Verify the uploaded object against what was authorised, then attach it',
      metadata: routeMeta('form16:generate', { ai: 'hidden' }),
    },

    getMyStatutorySummary: {
      method: 'GET',
      path: '/compliance/me/statutory',
      query: z.object({ fiscalYear: z.coerce.number().int().optional() }),
      responses: {
        200: myStatutorySummarySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "The caller's own YTD contributions, TDS, and Form 16 state",
      metadata: routeMeta('statutory.own:read'),
    },

    downloadMyForm16: {
      method: 'POST',
      path: '/compliance/me/form16/download',
      body: z.object({
        fiscalYear: z.number().int(),
        part: z.enum(['a', 'b']),
      }),
      responses: {
        200: filingDownloadSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** Not issued yet, or that part was never attached. */
        404: errorBodySchema,
      },
      summary: "A short-lived link to the caller's own Form 16, once it has been issued",
      metadata: routeMeta(null),
    },
  },
  { pathPrefix: '/payroll' },
);
